import { useState, useEffect, useRef } from "react";
import { useNotification } from "../../../context/notification-provider";
import {
  updateTemplate,
  saveTemplateValidCharacters,
  deleteTemplateValidCharacters,
} from "../services/template-service";
import { INITIAL_FORM_DATA } from "../constants";
import useValidCharacters from "./use-valid-characters";
import useAvailableValidCharacters from "./use-available-valid-characters";
import type {
  TemplateInformation,
  DataTypeInfo,
  ValidCharacterItem,
} from "../types/api-types";
import type { FormData } from "../template-configuration/template-advanced-config";

const useTemplateConfiguration = (mutate: () => Promise<unknown>) => {
  const { showNotification } = useNotification();
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateInformation | null>(null);
  const { validCharacters, isLoading: isLoadingValidCharacters } =
    useValidCharacters(selectedTemplate?.id);
  const { availableCharacters } = useAvailableValidCharacters();
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingValidatorValue, setPendingValidatorValue] = useState(false);
  const [templateBeingToggled, setTemplateBeingToggled] =
    useState<TemplateInformation | null>(null);

  // Track original characters to detect deletions
  const originalCharactersRef = useRef<ValidCharacterItem[]>([]);

  const [formData, setFormData] = useState<FormData>({
    ...INITIAL_FORM_DATA,
    dataType: null as DataTypeInfo | null,
  });

  useEffect(() => {
    if (
      selectedTemplate &&
      validCharacters?.results &&
      validCharacters?.results?.length > 0 &&
      formData?.validCharactersList.length === 0
    ) {
      // Store original characters for deletion tracking
      originalCharactersRef.current = validCharacters.results;
      setFormData((prev) => ({
        ...prev,
        validCharactersList: validCharacters?.results,
      }));
    }
  }, [
    selectedTemplate,
    formData?.validCharactersList.length,
    validCharacters?.results,
  ]);

  const handleTemplateChange = (
    _event: React.SyntheticEvent,
    newValue: TemplateInformation | null,
  ) => {
    setSelectedTemplate(newValue);
    originalCharactersRef.current = []; // Reset original characters
    if (newValue) {
      setFormData({
        useDataValidator: newValue.useDataValidator,
        minLength: newValue.minLength,
        maxLength: newValue.maxLength,
        allowLetters: newValue.allowLetters,
        allowNumbers: newValue.allowNumbers,
        allowSpaces: newValue.allowSpaces,
        allowFutureDates: newValue.allowFutureDates,
        dataType: newValue.dataType,
        validCharactersList: [],
        validRegexList: [],
        replacementCharacters: [],
        routines: [],
        validValues: [],
      });
    }
  };

  const handleToggleValidator = (checked: boolean) => {
    if (!selectedTemplate) return;
    setTemplateBeingToggled(selectedTemplate);
    setPendingValidatorValue(checked);
    setShowConfirmDialog(true);
  };

  const handleToggleValidatorFromTable = (
    template: TemplateInformation,
    checked: boolean,
  ) => {
    setTemplateBeingToggled(template);
    setPendingValidatorValue(checked);
    setShowConfirmDialog(true);
  };

  const handleChangeTemplate = () => {
    setSelectedTemplate(null);
    setFormData({
      ...INITIAL_FORM_DATA,
      dataType: null as DataTypeInfo | null,
    });
  };

  const handleConfirmToggle = async () => {
    if (!templateBeingToggled) return;

    setShowConfirmDialog(false);
    setIsSaving(true);

    try {
      const updatedTemplate: TemplateInformation = {
        ...templateBeingToggled,
        useDataValidator: pendingValidatorValue,
      };

      await updateTemplate(updatedTemplate);
      await mutate();

      // If the toggled template is the currently selected one, update formData
      if (selectedTemplate?.id === templateBeingToggled.id) {
        setFormData({
          ...formData,
          useDataValidator: pendingValidatorValue,
        });
      }

      showNotification({
        message: `Template ${pendingValidatorValue ? "activated" : "deactivated"} successfully`,
        severity: "success",
      });
    } catch (err) {
      showNotification({
        message: `Failed to update template. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
      setTemplateBeingToggled(null);
    }
  };

  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
  };

  const handleSaveValidCharacters = async (
    addedCharacters: ValidCharacterItem[],
    deletedCharacters: ValidCharacterItem[],
  ) => {
    if (!selectedTemplate) return;

    setIsSaving(true);
    try {
      // Delete removed characters one by one
      if (deletedCharacters.length > 0) {
        await Promise.all(
          deletedCharacters.map((character) =>
            deleteTemplateValidCharacters(character),
          ),
        );
      }

      // Save new characters with templateId set
      if (addedCharacters.length > 0) {
        const charactersWithTemplateId = addedCharacters.map((char) => ({
          ...char,
          templateId: selectedTemplate.id,
        }));
        await saveTemplateValidCharacters(charactersWithTemplateId);
      }

      await mutate();

      showNotification({
        message: "Valid characters updated successfully",
        severity: "success",
      });
    } catch (err) {
      showNotification({
        message: `Failed to update valid characters. ${(err as Error).message}`,
        severity: "error",
      });
      throw err; // Re-throw so the dialog can handle the error state
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    setIsSaving(true);
    try {
      const updatedTemplate: TemplateInformation = {
        id: selectedTemplate.id,
        description: selectedTemplate.description,
        minLength: formData.minLength,
        maxLength: formData.maxLength,
        allowLetters: formData.allowLetters,
        allowNumbers: formData.allowNumbers,
        allowSpaces: formData.allowSpaces,
        allowFutureDates: formData.allowFutureDates,
        dataType: formData.dataType || selectedTemplate.dataType,
        useDataValidator: formData.useDataValidator,
      };

      // Save template configuration
      await updateTemplate(updatedTemplate);

      // Find deleted characters (in original but not in current)
      const deletedCharacters = (originalCharactersRef.current || []).filter(
        (original) =>
          !formData.validCharactersList.some(
            (current) => current.id === original.id,
          ),
      );

      // Delete removed characters one by one
      if (deletedCharacters.length > 0) {
        await Promise.all(
          deletedCharacters.map((character) =>
            deleteTemplateValidCharacters(character),
          ),
        );
      }

      // Save new/updated valid characters with templateId set
      if (formData.validCharactersList.length > 0) {
        const validCharactersWithTemplateId = formData.validCharactersList.map(
          (char) => ({
            ...char,
            templateId: selectedTemplate.id,
          }),
        );
        await saveTemplateValidCharacters(validCharactersWithTemplateId);
      }

      await mutate();

      showNotification({
        message: "Template updated successfully",
        severity: "success",
      });
    } catch (err) {
      showNotification({
        message: `Failed to update template. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    selectedTemplate,
    formData,
    isSaving,
    showConfirmDialog,
    pendingValidatorValue,
    availableValidCharacters: availableCharacters?.results || [],
    isLoadingValidCharacters,
    setFormData,
    handleTemplateChange,
    handleToggleValidator,
    handleToggleValidatorFromTable,
    handleChangeTemplate,
    handleConfirmToggle,
    handleCancelToggle,
    handleSave,
    handleSaveValidCharacters,
  };
};

export default useTemplateConfiguration;
